/**
 * /profile-setup — one-screen, zero-friction profile capture.
 * Replaces the previous 4-step flow. Everything is on a single scroll page
 * with visible section headers, live BMR/BMI/timeline preview, and a single
 * "Finish setup" CTA that lands the user directly on /home.
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Flame, Target, Sparkles, Salad, Activity, HeartPulse } from "lucide-react";
import { useAuthStore } from "../../store/authStore";
import FloatingCTA from "../../components/layout/FloatingCTA";
import { IMG } from "../../utils/images";
import { projectWeeksToGoal } from "../../utils/calories";
import { toast } from "sonner";

const goals = [
  { id: "Lose Fat", label: "Lose Fat", img: IMG.goalLose },
  { id: "Gain Muscle", label: "Gain Muscle", img: IMG.goalGain },
  { id: "Improve Endurance", label: "Endurance", img: IMG.goalEndurance },
  { id: "Maintain Weight", label: "Maintain", img: IMG.goalMaintain },
];
const activityLevels = [
  { id: "Sedentary", desc: "Little to no exercise" },
  { id: "Lightly Active", desc: "1-3 days/week" },
  { id: "Moderately Active", desc: "3-5 days/week" },
  { id: "Very Active", desc: "6-7 days/week" },
];
const dietaryOptions = ["Vegetarian", "Vegan", "Non-Vegetarian", "Keto", "No Preference"];
const medicalConditionOptions = ["Diabetes", "High BP / Hypertension", "None"];
const allergyOptions = ["Nuts", "Dairy", "Gluten", "Shellfish", "Egg", "Soy"];

export default function ProfileSetup() {
  const navigate = useNavigate();
  const saveProfile = useAuthStore((s) => s.saveProfile);
  const user = useAuthStore((s) => s.user);

  const [data, setData] = useState({
    gender: "male",
    age: 28,
    height_cm: 175,
    weight_kg: 72,
    goal: "Lose Fat",
    activity_level: "Moderately Active",
    dietary_preference: "No Preference",
    goal_weight_kg: 68,
    medical_conditions: [],
    allergies: [],
  });
  const [otherAllergy, setOtherAllergy] = useState("");
  const [saving, setSaving] = useState(false);
  const update = (patch) => setData((p) => ({ ...p, ...patch }));
  const toggleInList = (field, value) =>
    setData((p) => {
      const list = p[field] || [];
      if (field === "medical_conditions" && value === "None") {
        return { ...p, medical_conditions: list.includes("None") ? [] : ["None"] };
      }
      const withoutNone = list.filter((v) => v !== "None");
      const next = withoutNone.includes(value)
        ? withoutNone.filter((v) => v !== value)
        : [...withoutNone, value];
      return { ...p, [field]: next };
    });

  const projectedWeeks = projectWeeksToGoal(data);
  const bmi = useMemo(() => {
    const h = data.height_cm / 100;
    return h > 0 ? +(data.weight_kg / (h * h)).toFixed(1) : 0;
  }, [data.height_cm, data.weight_kg]);
  const bmr = useMemo(() => {
    // Mifflin-St Jeor
    const s = data.gender === "female" ? -161 : 5;
    return Math.round(10 * data.weight_kg + 6.25 * data.height_cm - 5 * data.age + s);
  }, [data]);

  const first = (user?.name || "there").split(" ")[0];

  const finish = async () => {
    setSaving(true);
    try {
      const extraAllergy = otherAllergy.trim();
      const payload = {
        ...data,
        allergies: extraAllergy ? [...data.allergies, extraAllergy] : data.allergies,
      };
      await saveProfile(payload);
      toast.success("Profile saved — welcome to FitFrik");
      navigate("/home", { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 401) {
        toast.error("Session expired. Please sign in again.");
        navigate("/login", { replace: true });
      } else if (typeof detail === "string") toast.error(detail);
      else if (Array.isArray(detail) && detail[0]?.msg) toast.error(detail[0].msg);
      else toast.error("Could not save your profile. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen pb-32" style={{ background: "#F4F3F1" }} data-testid="profile-setup">
      {/* WOW hero */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        style={{
          background: "linear-gradient(135deg, #00D4FF 0%, #0FB5A6 100%)",
          padding: "36px 22px 42px",
          borderBottomLeftRadius: 28,
          borderBottomRightRadius: 28,
          color: "#0B0B0B",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div style={{ position: "absolute", top: -30, right: -20, width: 160, height: 160, borderRadius: "50%", background: "rgba(255,255,255,0.15)" }} />
        <div style={{ position: "absolute", bottom: -50, left: -30, width: 130, height: 130, borderRadius: "50%", background: "rgba(255,255,255,0.10)" }} />
        <div style={{ position: "relative" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 10px", borderRadius: 100, background: "rgba(11,11,11,0.14)", fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 10, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase" }}>
            <Sparkles size={12} /> One quick step
          </div>
          <h1 style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, fontSize: 34, letterSpacing: "-0.01em", lineHeight: 1.05, marginTop: 10 }}>
            Let&apos;s tailor FitFrik to you, {first}
          </h1>
          <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 13, marginTop: 6, opacity: 0.85 }}>
            Fill it in once — we&apos;ll do the math and unlock your daily FitScore.
          </p>
        </div>
      </motion.div>

      <div className="max-w-xl mx-auto px-5 pt-6" style={{ display: "flex", flexDirection: "column", gap: 22 }}>
        {/* You */}
        <Section title="About you" icon={<Activity size={14} color="#00B0D5" />}>
          <div>
            <FieldLabel>Gender</FieldLabel>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {["male", "female", "non-binary"].map((g) => (
                <Chip key={g} on={data.gender === g} onClick={() => update({ gender: g })} testid={`gender-${g}`}>
                  {g.replace("-", " ")}
                </Chip>
              ))}
            </div>
          </div>
          <NumField label="Age" value={data.age} onChange={(v) => update({ age: v })} min={13} max={100} unit="years" testid="age-input" />
          <NumField label="Height" value={data.height_cm} onChange={(v) => update({ height_cm: v })} min={120} max={220} unit="cm" testid="height-input" />
          <NumField label="Weight" value={data.weight_kg} onChange={(v) => update({ weight_kg: v })} min={30} max={200} unit="kg" testid="weight-input" />

          {/* live BMI / BMR chip */}
          <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
            <StatChip label="BMI" value={bmi} accent="#0FB5A6" />
            <StatChip label="BMR" value={`${bmr} kcal`} accent="#00D4FF" />
          </div>
        </Section>

        {/* Goal */}
        <Section title="Your goal" icon={<Target size={14} color="#7A67D5" />}>
          <div className="grid grid-cols-2 gap-3">
            {goals.map((g) => (
              <button
                key={g.id}
                type="button"
                onClick={() => update({ goal: g.id })}
                data-testid={`goal-${g.id.replace(/\s/g, "-").toLowerCase()}`}
                className="relative h-32 rounded-[16px] overflow-hidden border-2 transition-all"
                style={{ borderColor: data.goal === g.id ? "#00D4FF" : "transparent" }}
              >
                <img src={g.img} alt="" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.7) 100%)" }} />
                <div className="absolute bottom-3 left-3 text-white font-display text-[16px] text-left" style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700 }}>{g.label}</div>
                {data.goal === g.id && (
                  <div className="absolute top-2 right-2 w-6 h-6 rounded-full grid place-items-center" style={{ background: "#00D4FF" }}>
                    <span className="text-black font-bold text-xs">✓</span>
                  </div>
                )}
              </button>
            ))}
          </div>
        </Section>

        {/* Activity */}
        <Section title="Activity level" icon={<Flame size={14} color="#F5C518" />}>
          <div className="grid grid-cols-2 gap-2">
            {activityLevels.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => update({ activity_level: a.id })}
                data-testid={`activity-${a.id.replace(/\s/g, "-").toLowerCase()}`}
                className="text-left p-3 rounded-[12px] border transition-all"
                style={{
                  background: data.activity_level === a.id ? "rgba(0,212,255,0.1)" : "#FFFFFF",
                  borderColor: data.activity_level === a.id ? "#00D4FF" : "#E7E5E1",
                }}
              >
                <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: 15, color: "#1A1A1A" }}>{a.id}</div>
                <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 11, color: "#8A8884", marginTop: 2 }}>{a.desc}</div>
              </button>
            ))}
          </div>
        </Section>

        {/* Diet + goal weight */}
        <Section title="Diet & target" icon={<Salad size={14} color="#0FB5A6" />}>
          <div>
            <FieldLabel>Dietary preference</FieldLabel>
            <div className="flex flex-wrap gap-2 mt-2">
              {dietaryOptions.map((d) => (
                <Chip key={d} on={data.dietary_preference === d} onClick={() => update({ dietary_preference: d })} testid={`diet-${d.replace(/\s/g, "-").toLowerCase()}`}>
                  {d}
                </Chip>
              ))}
            </div>
          </div>
          <NumField label="Goal weight" value={data.goal_weight_kg} onChange={(v) => update({ goal_weight_kg: v })} min={30} max={200} unit="kg" testid="goal-weight-input" />

          {projectedWeeks && (
            <div
              className="rounded-[12px] px-4 py-3"
              style={{ background: "rgba(15,181,166,0.10)", borderLeft: "3px solid #0FB5A6" }}
            >
              <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: "#0B8477", fontWeight: 700 }}>
                Projected timeline
              </div>
              <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: 18, marginTop: 2, color: "#1A1A1A" }}>
                Reach goal in approximately {projectedWeeks} weeks
              </div>
            </div>
          )}
        </Section>

        {/* Health — powers personalized scan verdicts (good/limit/avoid) */}
        <Section title="Health profile" icon={<HeartPulse size={14} color="#D5527A" />}>
          <div>
            <FieldLabel>Medical conditions</FieldLabel>
            <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 11, color: "#8A8884", marginTop: 2, marginBottom: 6 }}>
              We use this to flag foods that aren&apos;t ideal for you.
            </p>
            <div className="flex flex-wrap gap-2">
              {medicalConditionOptions.map((m) => (
                <Chip
                  key={m}
                  on={data.medical_conditions.includes(m)}
                  onClick={() => toggleInList("medical_conditions", m)}
                  testid={`condition-${m.replace(/[\s/]+/g, "-").toLowerCase()}`}
                >
                  {m}
                </Chip>
              ))}
            </div>
          </div>
          <div>
            <FieldLabel>Allergies</FieldLabel>
            <p style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 11, color: "#8A8884", marginTop: 2, marginBottom: 6 }}>
              A scanned meal containing these will always be flagged as Avoid.
            </p>
            <div className="flex flex-wrap gap-2">
              {allergyOptions.map((a) => (
                <Chip
                  key={a}
                  on={data.allergies.includes(a)}
                  onClick={() => toggleInList("allergies", a)}
                  testid={`allergy-${a.toLowerCase()}`}
                >
                  {a}
                </Chip>
              ))}
            </div>
            <input
              type="text"
              value={otherAllergy}
              onChange={(e) => setOtherAllergy(e.target.value)}
              placeholder="Other allergy (optional)"
              data-testid="allergy-other-input"
              className="mt-2 w-full h-11 px-4 rounded-[12px] outline-none"
              style={{ background: "#F4F3F1", border: "1px solid #E7E5E1", fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 13 }}
            />
          </div>
        </Section>
      </div>

      <FloatingCTA label={saving ? "Saving…" : "Finish setup"} onClick={finish} disabled={saving} testid="profile-continue" />
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      style={{ background: "#FFFFFF", border: "1px solid #EEECE9", borderRadius: 18, padding: "16px 16px 18px" }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
        {icon}
        <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: "#8A8884", fontWeight: 700 }}>
          {title}
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>{children}</div>
    </motion.section>
  );
}

function FieldLabel({ children }) {
  return (
    <label style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 11, letterSpacing: "0.10em", textTransform: "uppercase", color: "#8A8884", fontWeight: 700 }}>
      {children}
    </label>
  );
}

function Chip({ on, onClick, testid, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="h-11 px-4 rounded-full border capitalize transition-all"
      style={{
        background: on ? "rgba(0,212,255,0.1)" : "#FFFFFF",
        borderColor: on ? "#00D4FF" : "#E7E5E1",
        color: "#1A1A1A",
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        fontWeight: on ? 700 : 500,
        fontSize: 13,
        borderRadius: 100,
      }}
    >
      {children}
    </button>
  );
}

function StatChip({ label, value, accent }) {
  return (
    <div
      style={{
        flex: 1,
        padding: "8px 12px",
        borderRadius: 12,
        background: `${accent}14`,
        border: `1px solid ${accent}44`,
      }}
    >
      <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 10, color: "#8A8884", fontWeight: 700, letterSpacing: "0.10em", textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800, fontSize: 20, color: "#1A1A1A", lineHeight: 1 }}>{value}</div>
    </div>
  );
}

function NumField({ label, value, onChange, min, max, unit, testid }) {
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <div className="mt-2 flex items-center gap-3 rounded-[12px] px-4 h-12 border" style={{ background: "#F4F3F1", borderColor: "#E7E5E1" }}>
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={String(value ?? "")}
          onChange={(e) => {
            const cleaned = (e.target.value || "").replace(/\D/g, "").replace(/^0+(?=\d)/, "");
            if (cleaned === "") { onChange(min); return; }
            const n = parseInt(cleaned, 10);
            if (!Number.isNaN(n)) onChange(n);
          }}
          onBlur={(e) => {
            const n = parseInt((e.target.value || "").replace(/\D/g, ""), 10);
            if (Number.isNaN(n) || n < min) onChange(min);
            else if (n > max) onChange(max);
          }}
          data-testid={testid}
          className="flex-1 bg-transparent outline-none"
          style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: 20, color: "#1A1A1A" }}
        />
        <span style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 12, color: "#8A8884" }}>{unit}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full mt-2"
        style={{ accentColor: "#00D4FF" }}
      />
    </div>
  );
}
